/**
 * Pure next-task selector for the guided Crew mobile workflow.
 * Maps attendance / trip / stop / scan / POD state to a single recommended action.
 */

export type CrewNextTaskKind =
  | 'clock_in'
  | 'start_trip'
  | 'arrive_stop'
  | 'start_service'
  | 'scan'
  | 'reconcile'
  | 'confirm_cargo'
  | 'end_service'
  | 'capture_pod'
  | 'depart_stop'
  | 'continue_trip'
  | 'request_rtb'
  | 'wait_rtb'
  | 'end_trip'
  | 'idle';

export type CrewNextTask = {
  kind: CrewNextTaskKind;
  label: string;
  stopId?: string;
  href?: string;
};

export type CrewNextTaskStop = {
  id: string;
  type: string;
  status: string;
  hasException?: boolean;
  podId?: string | null;
  /** Cargo check for no-manifest stops (null/absent = not needed). */
  cargoCheck?: CrewCargoCheckState | null;
};

/** Per-stop cargo check as sent by the server (only required/completed drive the task). */
export type CrewCargoCheckState = {
  required: boolean;
  completed: boolean;
  mode?: string;
  expectedPieces?: number | null;
  countedPieces?: number | null;
  picName?: string | null;
  mismatch?: boolean;
  mismatchReason?: string | null;
  confirmedAt?: string | null;
};

export type CrewNextTaskTrip = {
  id: string;
  status: string;
  scanningEnabled?: boolean;
  rtbApprovalState?: string | null;
  stops: CrewNextTaskStop[];
};

export type CrewNextTaskInput = {
  clockedIn: boolean;
  trip: CrewNextTaskTrip | null;
  /**
   * For the current servicing stop: true when scan counts reconcile
   * (no variance / unknown / pending) OR scanning is not required for UI purposes.
   * Callers derive this from counts + pending; do not invent server gates here.
   */
  scanReconciled?: boolean;
  /** True when overs / pending / wrong_stop / not_on_manifest need crew attention. */
  scanNeedsReconcile?: boolean;
  /** Next eligible trip on the same vehicle run, when known. */
  nextTripId?: string | null;
  nextTripNumber?: string | null;
};

const CREW_HREF = {
  home: '/crew',
  scan: '/crew/scan',
  pod: '/crew/pod',
} as const;

function task(
  kind: CrewNextTaskKind,
  label: string,
  extras?: { stopId?: string; href?: string },
): CrewNextTask {
  return { kind, label, ...extras };
}

/** First non-departed stop in sequence order (caller should already sort stops). */
function currentStop(stops: CrewNextTaskStop[]): CrewNextTaskStop | undefined {
  return stops.find((s) => s.status !== 'departed');
}

function remainingCustomerStops(stops: CrewNextTaskStop[]): CrewNextTaskStop[] {
  return stops.filter(
    (s) => (s.type === 'pickup' || s.type === 'drop') && s.status !== 'departed',
  );
}

function continueTripTask(input: CrewNextTaskInput): CrewNextTask {
  const label = input.nextTripNumber
    ? `Continue to ${input.nextTripNumber}`
    : 'Continue to next trip';
  return task('continue_trip', label, { href: CREW_HREF.home });
}

function hasNextTrip(input: CrewNextTaskInput): boolean {
  return Boolean(input.nextTripId || input.nextTripNumber);
}

/**
 * Resolve the single next Crew task from current operational state.
 */
export function resolveCrewNextTask(input: CrewNextTaskInput): CrewNextTask {
  if (!input.clockedIn) {
    return task('clock_in', 'Clock in', { href: CREW_HREF.home });
  }

  const trip = input.trip;
  if (!trip) {
    return task('idle', 'No active trip', { href: CREW_HREF.home });
  }

  if (trip.status === 'dispatched' || trip.status === 'assigned') {
    return task('start_trip', 'Start trip', { href: CREW_HREF.home });
  }

  if (trip.status === 'completed' || trip.status === 'cancelled') {
    if (hasNextTrip(input)) {
      return continueTripTask(input);
    }
    return task('idle', 'Trip complete', { href: CREW_HREF.home });
  }

  const stops = trip.stops ?? [];

  // After last customer stop (or when there are none left), prefer continuing the
  // vehicle run over depot / RTB ceremony when a next eligible trip exists.
  if (hasNextTrip(input) && remainingCustomerStops(stops).length === 0) {
    return continueTripTask(input);
  }

  const stop = currentStop(stops);
  if (!stop) {
    const rtb = trip.rtbApprovalState;
    if (rtb === 'pending') {
      return task('wait_rtb', 'Waiting for return-to-base approval', { href: CREW_HREF.home });
    }
    if (rtb === 'approved' || rtb === 'not_required' || !rtb) {
      return task('end_trip', 'End Vehicle Run', { href: CREW_HREF.home });
    }
    return task('request_rtb', 'Return to Base', { href: CREW_HREF.home });
  }

  // Depot / return-to-base: single Arrived at Base action — no service/depart theatre.
  if (stop.type === 'return_to_base') {
    if (stop.status === 'pending' || stop.status === 'arrived' || stop.status === 'servicing' || stop.status === 'serviced') {
      // pending = normal path; arrived/servicing/serviced = legacy mid-ceremony recovery
      // (server terminal-arrive accepts these and completes the stop).
      return task('arrive_stop', 'Arrived at Base', { stopId: stop.id, href: CREW_HREF.home });
    }
    return task('idle', 'Waiting', { stopId: stop.id, href: CREW_HREF.home });
  }

  switch (stop.status) {
    case 'pending':
      return task('arrive_stop', 'Mark arrived', { stopId: stop.id, href: CREW_HREF.home });
    case 'arrived':
      return task('start_service', 'Start servicing', { stopId: stop.id, href: CREW_HREF.home });
    case 'servicing': {
      const scanningOn = Boolean(trip.scanningEnabled);
      const reconciled = Boolean(input.scanReconciled);
      const hasException = Boolean(stop.hasException);

      if (scanningOn && !reconciled && !hasException) {
        if (input.scanNeedsReconcile) {
          return task('reconcile', 'Fix scan problems', {
            stopId: stop.id,
            href: CREW_HREF.scan,
          });
        }
        return task('scan', 'Scan cargo', { stopId: stop.id, href: CREW_HREF.scan });
      }
      // No-manifest stops: piece count or PIC confirmation before Mark serviced.
      // A filed exception waives it (same as the server gate).
      if (stop.cargoCheck?.required && !stop.cargoCheck.completed && !hasException) {
        return task('confirm_cargo', 'Confirm cargo', { stopId: stop.id, href: CREW_HREF.home });
      }
      return task('end_service', 'Mark serviced', { stopId: stop.id, href: CREW_HREF.home });
    }
    case 'serviced': {
      if (!stop.podId) {
        return task('capture_pod', 'Get signature', { stopId: stop.id, href: CREW_HREF.pod });
      }
      return task('depart_stop', 'Depart stop', { stopId: stop.id, href: CREW_HREF.home });
    }
    default:
      return task('idle', 'Waiting', { stopId: stop.id, href: CREW_HREF.home });
  }
}
